/**
 * Manager Access Management Component
 * 
 * Allows company owners to view and manage their managers
 * Integrated with VA and board member management
 */

import React, { useState, useEffect } from 'react';
import { Users, UserMinus, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { CompanyManagerRelation } from '../types/managerAccess.types';
import { getActiveManagers, revokeManagerAccess } from '../services/managerAccessService';

interface ManagerAccessManagementProps {
  companyOwnerId: string;
}

export const ManagerAccessManagement: React.FC<ManagerAccessManagementProps> = ({
  companyOwnerId
}) => {
  const [managers, setManagers] = useState<CompanyManagerRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState<CompanyManagerRelation | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadManagers();
  }, [companyOwnerId]);

  const loadManagers = async () => {
    try {
      setLoading(true);
      const activeManagers = await getActiveManagers(companyOwnerId);
      setManagers(activeManagers);
    } catch (error) {
      console.error('Error loading managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeClick = (manager: CompanyManagerRelation) => {
    setSelectedManager(manager);
    setShowRevokeModal(true);
  };

  const handleRevokeConfirm = async () => {
    if (!selectedManager || !revokeReason.trim()) {
      alert('⚠️ Geef een reden op voor het intrekken van de toegang.');
      return;
    }

    if (processing) return;

    try {
      setProcessing(true);
      await revokeManagerAccess(selectedManager.id, companyOwnerId, revokeReason);
      
      // Remove from list
      setManagers(prev => prev.filter(m => m.id !== selectedManager.id));
      
      // Reset modal
      setShowRevokeModal(false);
      setSelectedManager(null);
      setRevokeReason('');
      
      alert('✅ Manager toegang ingetrokken. De gebruiker heeft een notificatie ontvangen.');
    } catch (error) {
      console.error('Error revoking access:', error);
      alert('❌ Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Managers</h3>
        </div>
        <span className="text-sm text-gray-500">
          {managers.length} actieve manager{managers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Managers List */}
      {managers.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Geen actieve managers</p>
          <p className="text-sm text-gray-500 mt-1">
            Wanneer iemand toegang aanvraagt als manager, verschijnt dat hier.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {managers.map((manager) => (
            <div
              key={manager.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{manager.managerName}</h4>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Actief
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{manager.managerEmail}</p>
                  {manager.managerPhone && (
                    <p className="text-sm text-gray-500 mt-1">{manager.managerPhone}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Toegang verleend op {new Date(manager.grantedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>

                  {/* Permissions */}
                  {manager.permissions && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {manager.permissions.canPost && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                          Kan posten
                        </span>
                      )}
                      {manager.permissions.canManageContent && (
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                          Content beheer
                        </span>
                      )}
                      {manager.permissions.canInviteMembers && (
                        <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                          Leden uitnodigen
                        </span>
                      )}
                      {manager.permissions.canViewAnalytics && (
                        <span className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full">
                          Analytics inzien
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleRevokeClick(manager)}
                  className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Toegang intrekken"
                >
                  <UserMinus className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Toegang intrekken</h3>
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setSelectedManager(null);
                  setRevokeReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Je staat op het punt de toegang in te trekken voor:</p>
                    <p className="font-semibold">{selectedManager.managerName}</p>
                    <p className="mt-2">Deze actie kan niet ongedaan worden gemaakt. De manager verliest direct alle toegang.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reden voor intrekking *
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Bijv. Contract is beëindigd, functie is vervallen, etc."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deze reden wordt gedeeld met de manager
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setSelectedManager(null);
                    setRevokeReason('');
                  }}
                  disabled={processing}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleRevokeConfirm}
                  disabled={processing || !revokeReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Bezig...' : 'Intrekken'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerAccessManagement;
